import type { QueryClient } from "@tanstack/react-query";
import type { LoaderFunction } from "react-router-dom";

import { CollectionService } from "@/shared/domain/collections";
import { collectionQueryKeys } from "@/shared/domain/collections/hooks/collectionQueryKeys";
import { RobotoDomainException } from "@/shared/http/exceptions";
import { makeAuthenticatedHttpClient } from "@/shared/http/makeAuthenticatedHttpClient";
import { ErrorMonitoringService } from "@/shared/services";

/**
 * Loader for the collection detail route. Primes the TanStack Query cache
 * with the collection record so that the Collection component renders the
 * header immediately on mount without a skeleton or a redundant network call.
 *
 * On failure null is returned so the component handles the error state via
 * useCollection. Client errors (4xx) are swallowed silently; server/network
 * errors are reported to monitoring.
 */
export function loadCollectionRecord(queryClient: QueryClient): LoaderFunction {
  return async ({ params }) => {
    const { collectionId } = params;
    if (!collectionId) {
      return null;
    }

    try {
      await queryClient.ensureQueryData({
        queryKey: collectionQueryKeys.detail(collectionId),
        // httpClient is created inside queryFn so it is only instantiated
        // when a real network call is needed (cache miss or stale data).
        queryFn: async ({ signal }) => {
          const httpClient = await makeAuthenticatedHttpClient();
          const collectionService = new CollectionService(httpClient);
          return collectionService.getCollectionById(collectionId, {
            abortSignal: signal,
          });
        },
      });
    } catch (error) {
      const isClientError =
        error instanceof RobotoDomainException && error.isClientError();

      if (!isClientError) {
        ErrorMonitoringService.captureError(error, {
          context: "loadCollectionRecord",
          collectionId,
        });
      }
    }

    return null;
  };
}
